import type { RecentPlayedResponse } from '../types/game'
import { api } from './api-client'

// `/games/recent_played.php` is the single source of truth for the RECIENTES
// tab: one response carries both the play count (the tab badge) and the full
// game list (the grid). Because of that it has several independent callers that
// can fire on the same tick and produce byte-identical duplicate GETs:
//
//   - the category-list fetch, for the badge count (see gameCategories.ts)
//   - the bottom-nav RECIENTES tab's onLinkClick (a head-start refresh)
//   - the casino page's load effect, which always refetches the recent tab
//   - the top category bar (useCategoryChange) on a tab switch
//
// useGames' own in-flight guard cannot catch these: it is a per-hook-instance
// ref (bottom-nav and the casino page hold separate ones) and its key includes
// the `fresh` flag, which differs between those two callers. So the de-dupe
// lives here, at the request layer, where every caller passes through.
//
// In-flight only — never a cache. The entry is cleared once the request settles
// so the next visit to the tab still refetches (recent history goes stale the
// moment the user opens a game). Keyed by account so a request issued for one
// user is never handed to another after a logout/account switch.
let inFlight: {
  account: string
  promise: Promise<RecentPlayedResponse | null>
} | null = null

export const fetchRecentPlayed = (
  username: string,
): Promise<RecentPlayedResponse | null> => {
  if (inFlight && inFlight.account === username) return inFlight.promise

  const promise = api
    .get<RecentPlayedResponse>('/games/recent_played.php', {
      account: username,
    })
    .then(res => res.data ?? null)
    .finally(() => {
      // Only clear if this is still the current entry (a newer request for a
      // different account may have replaced it).
      if (inFlight?.promise === promise) inFlight = null
    })

  inFlight = { account: username, promise }

  return promise
}
